// This is a TypeScript code snippet for an Angular service
// `Injectable` decorator marks it as a service that can be injected into other Angular components
import { Injectable } from '@angular/core';
// `HttpClient`, `HttpEvent`, `HttpHeaders`, `HttpResponse` are imported from `@angular/common/http` for making HTTP requests
import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
// `Observable` is imported from `rxjs` for handling asynchronous operations
import { Observable } from 'rxjs';
// `environment` is imported from the environments file. This is typically where you define environment variables like API endpoints
import { environment } from '../../../environments/environment';
// `Configuration` is a custom type imported from `@app/common/configuration`
import { Configuration } from '@app/common/configuration';
// `apiResponseFormat` is a custom type imported from `@app/common/variables`
import { apiResponseFormat } from '@app/common/variables';

// This is a TypeScript decorator `@Injectable` used in Angular
// `@Injectable` marks a class as available to an injector for instantiation and allows the injection of dependencies into the constructor of the class
@Injectable({
  // `{ providedIn: 'root' }` means that the service is provided in the root injector. Angular creates a single, shared instance of the service and injects it into any class that asks for it. Registering the provider in the `@Injectable` metadata also allows Angular to optimize an app by removing the service from the compiled app if it isn't used
  providedIn: 'root'
})

// This is a TypeScript class for the `AddViewReleaseListService` service
export class AddViewReleaseListService {
  // `defaultHeaders` is a public property of type `HttpHeaders`. This is used to set default headers for HTTP requests
  public defaultHeaders = new HttpHeaders();
  // `configuration` is a public property of type `Configuration`. This can be used to store configuration settings
  public configuration = new Configuration();
  // `authServerUrl` is a property that holds the base URL for the authentication server. This is fetched from the environment variables
  authServerUrl = environment.API_APP_PATH;
  // The `constructor` method is a special method for creating and initializing an object created with a class. Here, `HttpClient` is injected into the service, which allows for making HTTP requests
  constructor(private readonly httpClient: HttpClient) {

  }


  /* This is a TypeScript method overloading for the `releasedetailslist` method in the `AddViewReleaseListService` service
    Method overloading allows multiple methods with the same name but different parameters. TypeScript uses the number of parameters and their types to determine which method to call
    The `releasedetailslist` method takes four parameters: `inputRequest`, `observe`, `reportProgress`, and `options`
    `inputRequest` is the request payload
    `observe` is an optional parameter that determines the return type of the method. It can be 'body', 'response', or 'events'
    `reportProgress` is an optional boolean parameter that indicates whether or not to report the progress of the HTTP request
    `options` is an optional parameter that can contain additional HTTP headers
    Depending on the value of `observe`, the method returns an `Observable` of different types: `any`, `HttpResponse<any>`, or `HttpEvent<any>`
  */
  public releasedetailslist(inputRequest: any, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any>;
  public releasedetailslist(inputRequest: any, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<any>>;
  public releasedetailslist(inputRequest: any, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<any>>;
  /**
   * Retrieves the list of release details based on the provided input request.
   * 
   * @param inputRequest - The input request for retrieving release details.
   * @param observe - The type of response observation. Defaults to 'body'.
   * @param reportProgress - Whether to report progress of the HTTP request. Defaults to false.
   * @param options - Additional options for the HTTP request.
   * @returns An observable that emits the response containing the release details.
   */
  public releasedetailslist(inputRequest: any, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {

    // `headers` is a local variable that is assigned the value of `defaultHeaders`, which is a property of the current class instance (`this`)
    let headers = this.defaultHeaders;
    // `httpHeaderAcceptSelected` is a local variable that is assigned the value of `options.httpHeaderAccept` if `options` is defined, otherwise it is `undefined`. The type of `httpHeaderAcceptSelected` is either `string` or `undefined`
    let httpHeaderAcceptSelected: string | undefined = options?.httpHeaderAccept;

    // The `if` statement checks if `httpHeaderAcceptSelected` is `undefined`
    if (httpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts: string[] = [
        'text/plain',
        'application/json',
        'text/json'
      ];
      // `httpHeaderAcceptSelected` is then assigned the value returned by the `selectHeaderAccept` method of the `configuration` object. This method is presumably used to select the appropriate 'Accept' header based on the `httpHeaderAccepts` array
      httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    // The `if` statement checks if `httpHeaderAcceptSelected` is not `undefined`
    if (httpHeaderAcceptSelected !== undefined) {
      // The `set` method returns a new `HttpHeaders` instance with the new header, so `headers` is reassigned to this new instance
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // `responseType` is a local variable that is initially set to 'json'. The type of `responseType` is either 'text' or 'json'
    let responseType: 'text' | 'json' = 'json';
    // The `if` statement checks if `httpHeaderAcceptSelected` is truthy (not `null`, `undefined`, `false`, `0`, `NaN`, or an empty string) and if `httpHeaderAcceptSelected` starts with 'text'
    if (httpHeaderAcceptSelected?.startsWith('text')) {
      // If both conditions in the `if` statement are true, `responseType` is set to 'text'
      responseType = 'text';
    }

    // The `post` method of the `httpClient` object is called to make a POST request
    return this.httpClient.post<any>(
      `${this.authServerUrl}/api/app/release-detail/release-details-list`,
      inputRequest,
      {
        // `responseType`: the expected response type of the request. It is cast to `any` because TypeScript expects a specific set of possible values for `responseType`
        responseType: responseType as any,
        // `withCredentials`: a boolean that indicates whether or not to send credentials (like cookies or authorization headers) with the request
        withCredentials: false,
        // `headers`: the headers to be included in the request
        headers: headers,
        // `observe`: the type of response to observe
        observe: observe,
        // `reportProgress`: a boolean that indicates whether or not to report the progress of the request
        reportProgress: reportProgress,
      }
    );
  }

  public callReleaseApi(apiEndpoint: any, paylod:any, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any>;
  public callReleaseApi(apiEndpoint: any, paylod:any, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpResponse<any>>;
  public callReleaseApi(apiEndpoint: any, paylod:any, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<HttpEvent<any>>;
  public callReleaseApi(apiEndpoint: any, paylod:any, observe: any = 'body', reportProgress: boolean = false, options?: { httpHeaderAccept?: apiResponseFormat }): Observable<any> {

    // `headers` is a local variable that is assigned the value of `defaultHeaders`, which is a property of the current class instance (`this`)
    let headers = this.defaultHeaders;
    // `httpHeaderAcceptSelected` is a local variable that is assigned the value of `options.httpHeaderAccept` if `options` is defined, otherwise it is `undefined`. The type of `httpHeaderAcceptSelected` is either `string` or `undefined`
    let httpHeaderAcceptSelected: string | undefined = options?.httpHeaderAccept;

    // The `if` statement checks if `httpHeaderAcceptSelected` is `undefined`
    if (httpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts: string[] = [
        'text/plain',
        'application/json',
        'text/json'
      ];
      // `httpHeaderAcceptSelected` is then assigned the value returned by the `selectHeaderAccept` method of the `configuration` object. This method is presumably used to select the appropriate 'Accept' header based on the `httpHeaderAccepts` array
      httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    // The `if` statement checks if `httpHeaderAcceptSelected` is not `undefined`
    if (httpHeaderAcceptSelected !== undefined) {
      // The `set` method returns a new `HttpHeaders` instance with the new header, so `headers` is reassigned to this new instance
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // `responseType` is a local variable that is initially set to 'json'. The type of `responseType` is either 'text' or 'json'
    let responseType: 'text' | 'json' = 'json';
    // The `if` statement checks if `httpHeaderAcceptSelected` is truthy (not `null`, `undefined`, `false`, `0`, `NaN`, or an empty string) and if `httpHeaderAcceptSelected` starts with 'text'
    if (httpHeaderAcceptSelected?.startsWith('text')) {
      // If both conditions in the `if` statement are true, `responseType` is set to 'text'
      responseType = 'text';
    }

    // The `post` method of the `httpClient` object is called to make a POST request
    return this.httpClient.post<any>(
      `${this.authServerUrl}/api/app/${apiEndpoint}`,
      paylod, // <-- use payload here
      {
        // `responseType`: the expected response type of the request. It is cast to `any` because TypeScript expects a specific set of possible values for `responseType`
        responseType: responseType as any,
        // `withCredentials`: a boolean that indicates whether or not to send credentials (like cookies or authorization headers) with the request
        withCredentials: false,
        // `headers`: the headers to be included in the request
        headers: headers,
        // `observe`: the type of response to observe
        observe: observe,
        // `reportProgress`: a boolean that indicates whether or not to report the progress of the request
        reportProgress: reportProgress,
      }
    );
  }
}